import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { FaSave, FaArrowLeft, FaPlus, FaTimes, FaUpload, FaSpinner, FaTrash } from 'react-icons/fa'
import { Card, Banner, Spinner, PageHeader, Toast, useToast } from '../components/ui'
import { money } from '../lib/format'
import { productImageUrl } from '../lib/images'
import StockGrid, { gridToRows, rowsToGrid } from '../components/StockGrid'
import DeleteProductDialog from '../components/DeleteProductDialog'
import * as api from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { SHOW_STOCK } from '../lib/features'

/**
 * Add or edit a product.
 *
 * This is the screen that makes the whole system worth building: saving here with
 * status "active" puts the garment on the shop on the next page load, with no
 * deploy and no developer.
 *
 * Sizes and colours are edited as chip lists rather than comma-separated text.
 * Free text invites "S, M, L," and " XL" — a trailing empty size and a leading
 * space become a broken size button and a size nobody can select.
 */

const BLANK = {
  name: '',
  sku: '',
  description: '',
  brand: '',
  price: '',
  compare_at_price: '',
  cost_price: '',
  primary_category_id: '',
  image: '',
  images: [],
  sizes: [],
  colors: [],
  color_swatches: {},
  color_images: {},
  variants: [],
  status: 'draft',
  featured: false,
  // Off while stock is hidden, so a product added today behaves like everything
  // else in the catalogue: always orderable, because the supplier delivers it.
  // Back to true when SHOW_STOCK is — see lib/features.js.
  track_inventory: SHOW_STOCK,
  sort_order: 0,
}

const ProductEditorPage = () => {
  // Staff may look but not change: product and price writes need is_manager.
  const readOnly = !useAuth().canManage
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = !id
  const { toast, notify, notifyError, dismiss } = useToast()

  const [form, setForm] = useState(BLANK)
  const [categories, setCategories] = useState([])
  const [allProducts, setAllProducts] = useState([])
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [notFound, setNotFound] = useState(false)
  // The stock grid is held apart from `form`: it writes to the inventory
  // table through its own RPC, not to the products row.
  const [stock, setStock] = useState({})
  const [stockLoaded, setStockLoaded] = useState(false)
  const [removing, setRemoving] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [cats, products] = await Promise.all([api.getCategories(), api.getProducts()])
        if (cancelled) return
        setCategories(cats ?? [])
        setAllProducts(products ?? [])
        if (isNew) setStockLoaded(true)

        if (!isNew) {
          // A product that is not there comes back as PostgREST's PGRST116,
          // which the api layer turns into a message about row level security.
          // That is right for a refused update and wrong here, where the plain
          // truth is that the product has been deleted.
          const product = await api.getProduct(id).catch((err) => {
            if (err?.cause?.code === 'PGRST116') return null
            throw err
          })
          if (cancelled) return
          if (!product) {
            setNotFound(true)
          } else {
            const existing = SHOW_STOCK ? await api.getProductInventory(id).catch(() => []) : []
            if (cancelled) return
            setStock(rowsToGrid(existing))
            setStockLoaded(true)
            setForm({
              ...BLANK,
              ...product,
              // Every field an input is bound to has to be a string, because
              // `...product` overwrites the blanks above with whatever the
              // database holds — and a column that is simply not filled in is
              // null, not ''. Left as null, React treats the input as
              // uncontrolled and save() throws on null.trim(), which took out
              // every product with no brand: 40 of the 93 in the catalogue.
              name: product.name ?? '',
              sku: product.sku ?? '',
              brand: product.brand ?? '',
              description: product.description ?? '',
              image: product.image ?? '',
              // Numeric fields come back as numbers but the inputs are text;
              // null would render the literal string "null" in the box.
              price: product.price ?? '',
              compare_at_price: product.compare_at_price ?? '',
              cost_price: product.cost_price ?? '',
              primary_category_id: product.primary_category_id ?? '',
            })
          }
        }
      } catch (err) {
        if (!cancelled) setError(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [id, isNew])

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  /**
   * Warn, do not block, on a reused style code.
   *
   * Coquis genuinely reuses one code across the short and long sleeve cut of the
   * same shirt, so a hard uniqueness check would make legitimate products
   * unsaveable. Flagging it lets the operator tell a deliberate reuse from a typo.
   */
  const skuClash = form.sku
    ? allProducts.filter((p) => p.sku === form.sku && p.id !== id)
    : []

  const save = async (statusOverride) => {
    setSaving(true)
    setError(null)
    try {
      if (!(form.name ?? '').trim()) throw new Error('A product needs a name.')
      if (form.price === '' || Number(form.price) < 0) throw new Error('Enter a price of zero or more.')
      if (!(form.sku ?? '').trim()) throw new Error('A product needs a style code.')

      const payload = {
        ...form,
        status: statusOverride ?? form.status,
        // `?? ''` as well as the coercion on load: this runs on whatever is in
        // the form, and a save must never be lost to a field that arrived null
        // by some other route.
        name: (form.name ?? '').trim(),
        sku: (form.sku ?? '').trim(),
        brand: (form.brand ?? '').trim() || null,
        description: (form.description ?? '').trim() || null,
        price: Number(form.price),
        // Empty string is not zero. Sending 0 would display a struck-through
        // "R0.00" as a fake original price.
        compare_at_price: form.compare_at_price === '' ? null : Number(form.compare_at_price),
        cost_price: form.cost_price === '' ? null : Number(form.cost_price),
        primary_category_id: form.primary_category_id || null,
        image: (form.image ?? '').trim() || null,
      }

      const saved = await api.saveProduct(isNew ? null : id, payload)

      // Stock is written second, and only if something was entered — it needs
      // the product's id, which does not exist until the insert above returns.
      // With stock hidden the grid is never rendered, so `rows` is empty and
      // this is skipped; the SHOW_STOCK test says so out loud.
      const rows = SHOW_STOCK ? gridToRows(stock) : []
      if (rows.length > 0) {
        try {
          await api.saveProductStock(saved.id, rows)
        } catch (stockErr) {
          // The product itself saved. Say so, rather than showing a bare failure
          // that makes it look like nothing was kept.
          notifyError(new Error(`${saved.name} saved, but the stock did not: ${stockErr.message}`))
          if (isNew) navigate(`/products/${saved.id}`, { replace: true })
          return
        }
      }

      notify(isNew ? `${saved.name} created.` : `${saved.name} saved.`)
      if (isNew) navigate(`/products/${saved.id}`, { replace: true })
    } catch (err) {
      setError(err)
      notifyError(err)
    } finally {
      setSaving(false)
    }
  }

  const upload = async (event) => {
    const files = [...(event.target.files ?? [])]
    if (files.length === 0) return
    setUploading(true)
    try {
      const urls = []
      for (const file of files) {
        urls.push(await api.uploadProductImage(file, { productSku: form.sku }))
      }
      setForm((f) => ({
        ...f,
        // First upload on a product with no photo becomes the main image, which
        // is almost always what was meant.
        image: f.image || urls[0],
        images: [...(f.images ?? []), ...urls],
      }))
      notify(`${urls.length} image${urls.length === 1 ? '' : 's'} uploaded.`)
    } catch (err) {
      notifyError(err)
    } finally {
      setUploading(false)
      event.target.value = '' // let the same file be picked again after a failure
    }
  }

  if (loading) return <Spinner label="Loading the product…" />

  // An empty form for a product that does not exist is worse than no form: it
  // invites someone to type a page of detail into a save that cannot land.
  if (notFound) {
    return (
      <div>
        <PageHeader title="Product not found" subtitle="Nothing is saved on this page">
          <Link to="/products" className="btn-ghost">
            <FaArrowLeft className="text-[11px]" />
            Back to the catalogue
          </Link>
        </PageHeader>
        <Banner tone="warning">
          That product could not be found. It has probably been deleted. Anything else you were
          looking for is in the catalogue.
        </Banner>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title={isNew ? 'Add a product' : form.name || 'Edit product'}
        subtitle={isNew ? 'It stays a draft until you publish it' : `Style code ${form.sku}`}
      >
        <Link to="/products" className="btn-ghost">
          <FaArrowLeft className="text-[11px]" />
          Back
        </Link>
        {readOnly ? null : form.status !== 'active' ? (
          <button onClick={() => save('active')} disabled={saving} className="btn-primary">
            {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
            Save &amp; publish
          </button>
        ) : (
          <button onClick={() => save()} disabled={saving} className="btn-primary">
            {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
            Save changes
          </button>
        )}
      </PageHeader>

      {api.isDemo && (
        <div className="mb-4">
          <Banner tone="info">
            Demo mode — you can explore this form, but saving is disabled until Supabase is connected.
          </Banner>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <Banner tone="error">{error.message}</Banner>
        </div>
      )}

      {readOnly && (
        <div className="mb-4">
          <Banner tone="info">
            View only. Products and prices are changed by the owner or a manager
            {SHOW_STOCK ? ' — to change stock levels, use Inventory' : ''}.
          </Banner>
        </div>
      )}

      {/* A disabled fieldset disables every input and button inside it, so the
          read-only view needs no per-control changes. */}
      <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">

      <div className="grid gap-4 lg:grid-cols-3">
        {/* ---- Main details ------------------------------------------- */}
        <div className="space-y-4 lg:col-span-2">
          <Card title="Details">
            <div className="space-y-3.5">
              <div>
                <label className="label" htmlFor="name">Product name</label>
                <input
                  id="name"
                  className="input"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. Carter Pique Golfer"
                />
              </div>

              <div className="grid gap-3.5 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="sku">Style code</label>
                  <input
                    id="sku"
                    className="input"
                    value={form.sku}
                    onChange={(e) => set('sku', e.target.value)}
                    placeholder="e.g. POLO-CARTER"
                  />
                  {skuClash.length > 0 && (
                    <p className="field-hint text-status-warning">
                      Also used by {skuClash.map((p) => p.name).join(', ')}. That is fine if this is
                      the other sleeve length of the same style.
                    </p>
                  )}
                </div>

                <div>
                  <label className="label" htmlFor="brand">Brand</label>
                  <input
                    id="brand"
                    className="input"
                    value={form.brand ?? ''}
                    onChange={(e) => set('brand', e.target.value)}
                    placeholder="e.g. Lorenzini"
                  />
                </div>
              </div>

              <div>
                <label className="label" htmlFor="description">Description</label>
                <textarea
                  id="description"
                  rows={3}
                  className="input"
                  value={form.description ?? ''}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="What the shopper sees on the product page."
                />
              </div>

              <div>
                <label className="label" htmlFor="category">Category</label>
                <select
                  id="category"
                  className="input"
                  value={form.primary_category_id ?? ''}
                  onChange={(e) => set('primary_category_id', e.target.value)}
                >
                  <option value="">Choose a category…</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="field-hint">
                  Decides which filter the product appears under in the shop.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Pricing">
            <div className="grid gap-3.5 sm:grid-cols-3">
              <div>
                <label className="label" htmlFor="price">Selling price (R)</label>
                <input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  className="input"
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="compare">Was (R)</label>
                <input
                  id="compare"
                  type="number"
                  step="0.01"
                  min="0"
                  className="input"
                  value={form.compare_at_price ?? ''}
                  onChange={(e) => set('compare_at_price', e.target.value)}
                  placeholder="Optional"
                />
                <p className="field-hint">Shown struck through. Leave blank for no sale price.</p>
              </div>
              <div>
                <label className="label" htmlFor="cost">Cost price (R)</label>
                <input
                  id="cost"
                  type="number"
                  step="0.01"
                  min="0"
                  className="input"
                  value={form.cost_price ?? ''}
                  onChange={(e) => set('cost_price', e.target.value)}
                  placeholder="Optional"
                />
                <p className="field-hint">
                  Never shown to customers. Used for profit reporting.
                </p>
              </div>
            </div>

            {form.price !== '' && form.cost_price !== '' && Number(form.cost_price) > 0 && (
              <p className="mt-3 text-2xs text-ink-secondary">
                Margin{' '}
                <strong className="tabular-nums">
                  {money(Number(form.price) - Number(form.cost_price))}
                </strong>{' '}
                per unit ·{' '}
                {(((Number(form.price) - Number(form.cost_price)) / Number(form.price)) * 100).toFixed(1)}%
              </p>
            )}
          </Card>

          <Card title="Sizes and colours" subtitle="What the shopper can choose on the product page">
            <div className="space-y-4">
              <ChipEditor
                label="Sizes"
                values={form.sizes ?? []}
                onChange={(v) => set('sizes', v)}
                placeholder="e.g. XL"
                hint="Shoppers must pick a size before they can add to the basket."
              />
              <ChipEditor
                label="Colours"
                values={form.colors ?? []}
                onChange={(v) => set('colors', v)}
                placeholder="e.g. Navy"
              />
            </div>
          </Card>

          {/* Stock lives on this page, not behind a separate action. "How many
              do I have" is part of adding a product, and the previous design —
              a button in the sidebar that silently created empty rows — meant a
              new product simply never appeared on the Inventory page. */}
          {SHOW_STOCK && form.track_inventory && (
            <Card
              title="Stock on hand"
              subtitle={
                form.sizes?.length || form.colors?.length
                  ? 'How many of each size and colour you have right now'
                  : 'How many you have right now'
              }
            >
              {stockLoaded ? (
                <StockGrid
                  sizes={form.sizes ?? []}
                  colors={form.colors ?? []}
                  values={stock}
                  onChange={setStock}
                  disabled={saving}
                />
              ) : (
                <p className="py-4 text-center text-2xs text-ink-muted">Loading stock…</p>
              )}
            </Card>
          )}
        </div>

        {/* ---- Sidebar ------------------------------------------------ */}
        <div className="space-y-4">
          <Card title="Visibility">
            <div className="space-y-3">
              <div>
                <label className="label" htmlFor="status">Status</label>
                <select
                  id="status"
                  className="input"
                  value={form.status}
                  onChange={(e) => set('status', e.target.value)}
                >
                  <option value="draft">Draft — hidden from the shop</option>
                  <option value="active">Live — visible on the shop</option>
                  <option value="archived">Archived — hidden, kept for records</option>
                </select>
              </div>

              <label className="flex items-center gap-2.5 text-[13px]">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => set('featured', e.target.checked)}
                  className="h-4 w-4 rounded border-hairline text-brand-navy focus:ring-brand-navy/30"
                />
                Show a “Featured” flash on the card
              </label>

              {SHOW_STOCK && (
                <label className="flex items-start gap-2.5 text-[13px]">
                  <input
                    type="checkbox"
                    checked={form.track_inventory}
                    onChange={(e) => set('track_inventory', e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-hairline text-brand-navy focus:ring-brand-navy/30"
                  />
                  <span>
                    Track stock levels
                    <span className="mt-0.5 block text-2xs text-ink-muted">
                      Turn off for made-to-order or tailoring, which never runs out.
                    </span>
                  </span>
                </label>
              )}

            </div>
          </Card>

          {/* Only when editing. On a new product there is nothing to remove,
              and a delete button beside an unsaved form is an invitation to
              lose work. */}
          {!isNew && !readOnly && (
            <Card title="Remove this product">
              <p className="text-2xs text-ink-muted">
                Take it off the shop, or delete it outright. The next screen shows what it is
                attached to before you choose.
              </p>
              <button onClick={() => setRemoving(true)} className="btn-ghost mt-2.5 w-full text-brand-red">
                <FaTrash className="text-[11px]" />
                Archive or delete
              </button>
            </Card>
          )}

          <Card title="Photographs">
            <div className="space-y-3">
              {form.image ? (
                <div className="overflow-hidden rounded-lg border border-hairline bg-plane">
                  <img src={productImageUrl(form.image)} alt="" className="h-40 w-full object-contain" />
                </div>
              ) : (
                <div className="grid h-40 place-items-center rounded-lg border border-dashed border-baseline bg-plane text-2xs text-ink-muted">
                  No main photo yet
                </div>
              )}

              <div>
                <label className="label" htmlFor="image">Main image URL</label>
                <input
                  id="image"
                  className="input"
                  value={form.image ?? ''}
                  onChange={(e) => set('image', e.target.value)}
                  placeholder="/assets/images/products/…"
                />
                <p className="field-hint">
                  Existing products use a path from the site. Uploads below get a full URL.
                </p>
              </div>

              <label className="btn-ghost w-full cursor-pointer">
                {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload className="text-[11px]" />}
                {uploading ? 'Uploading…' : 'Upload photographs'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/avif"
                  multiple
                  hidden
                  onChange={upload}
                  disabled={uploading}
                />
              </label>
              <p className="field-hint">JPEG, PNG, WebP or AVIF · up to 10 MB each</p>

              {(form.images ?? []).length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {form.images.map((url) => (
                    <div key={url} className="group relative overflow-hidden rounded-md border border-hairline bg-plane">
                      <img src={productImageUrl(url)} alt="" className="h-16 w-full object-contain" />
                      <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-black/55 p-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          onClick={() => set('image', url)}
                          className="text-[9px] font-semibold text-white hover:underline"
                        >
                          Main
                        </button>
                        <button
                          onClick={() => set('images', form.images.filter((u) => u !== url))}
                          className="text-white"
                          aria-label="Remove"
                        >
                          <FaTimes className="text-[9px]" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      </fieldset>

      <DeleteProductDialog
        product={isNew ? null : { id, name: form.name }}
        open={removing}
        onClose={() => setRemoving(false)}
        onDone={() => navigate('/products', { replace: true })}
      />

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

/**
 * Edit a string array as removable chips.
 *
 * Enter and comma both commit, because both are what people reach for. Duplicates
 * are dropped silently rather than rejected — adding "M" twice is a slip, not a
 * decision worth an error message.
 */
const ChipEditor = ({ label, values, onChange, placeholder, hint }) => {
  const [draft, setDraft] = useState('')

  const commit = () => {
    const value = draft.trim()
    if (!value) return
    if (!values.includes(value)) onChange([...values, value])
    setDraft('')
  }

  return (
    <div>
      <span className="label">{label}</span>

      {values.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {values.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-plane px-2 py-1 text-2xs font-medium text-ink"
            >
              {value}
              <button
                onClick={() => onChange(values.filter((v) => v !== value))}
                className="text-ink-muted hover:text-brand-red"
                aria-label={`Remove ${value}`}
              >
                <FaTimes className="text-[8px]" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          className="input input-sm flex-1"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              commit()
            }
          }}
          // Committing on blur means a typed-but-unconfirmed value is not lost
          // when the operator tabs away or clicks Save.
          onBlur={commit}
        />
        <button onClick={commit} className="btn-ghost btn-sm" aria-label={`Add ${label}`}>
          <FaPlus className="text-[9px]" />
        </button>
      </div>

      {hint && <p className="field-hint">{hint}</p>}
    </div>
  )
}

export default ProductEditorPage
